import { useCallback, useMemo } from 'react';
import TaskList from '@tiptap/extension-task-list';
import ListKeymap from '@tiptap/extension-list-keymap';
import UniqueID from '@tiptap-pro/extension-unique-id';

import { Extensions } from '@tiptap/core';
import randomString from '../../../../common/uid/randomString';
import { TaskListDocument } from '../../../../common/tiptap/extensions/taskList/TaskListDocument';
import { TaskListTitle } from '../../../../common/tiptap/extensions/taskList/TaskListTitle';
import { TaskListItem } from '../../../../common/tiptap/extensions/taskList/TaskListItem';
import type { TaskItemAssignmentShortcutContext } from '../../../../common/tiptap/extensions/taskList/taskListItemKeyboardHandlers';
import isSingleEmptyTaskListEditor from '../../../../common/tiptap/extensions/taskList/isSingleEmptyTaskListEditor';
import TaskTrashIcon from '../../../components/icons/light/medium/jsx/task-trash';
import TaskItemChipsContainer from '../../chipsPanel/TaskItemChipsContainer';
import { selectElementChip } from '../../chipsPanel/elementChipsActions';
import {
    buildTaskItemAssignmentChipDescriptor,
    buildTaskItemAssignmentPlaceholderChipDescriptor,
} from '../../chipsPanel/taskItemChipsUtils';
import TaskListDragHandle from './TaskListDragHandle';
import type { PlaceholderOptions } from '@tiptap/extension-placeholder';
import useClientCommonTiptapEditorExtensions, {
    UseClientCommonTiptapEditorExtensionsArgs,
} from '../../../components/tiptapEditor/useClientCommonTiptapEditorExtensions';

// The placeholder is node-aware: the title node and task item nodes show different prompts.
// Provided to the common Placeholder extension via `placeholderOptions`.
const taskListPlaceholder: NonNullable<PlaceholderOptions['placeholder']> = ({ node }) =>
    node.type.name === 'taskListTitle' ? 'New To-do List' : 'Add a task...';

type UseTaskListEditorExtensionsArgs = UseClientCommonTiptapEditorExtensionsArgs & {
    // Whether the task items can be modified — chips edited, items dragged out (from the board's write permission).
    canModifyContent: boolean;
    // The owning canvas element whose single-selection gates task-item chip activation.
    elementId: string;
    // Whether the owning task-list element is the current single selection.
    isElementSingleSelected: boolean;
};

/**
 * Gets the extensions for the standalone task list editor.
 *
 * Composes Milanote's shared client/common Tiptap extensions (which supply the StarterKit
 * nodes, marks, Placeholder, and the ExternalStoreSynchroniser that persists content) with
 * the task-list-specific document/title/list/item nodes.
 */
const useTaskListEditorExtensions = ({
    saveContent,
    isEditable,
    dispatch,
    onHighlightsChanged,
    onEmptyBackspace,
    canModifyContent,
    elementId,
    isElementSingleSelected,
}: UseTaskListEditorExtensionsArgs): Extensions => {
    // Opens the assignee picker when "@" is typed in a task item, by selecting
    // the task's assignment chip (the shortcut handler has already ensured
    // `showAssignments`, so the chip renders).
    const onAssignmentShortcut = useCallback(
        ({ taskId, assignments }: TaskItemAssignmentShortcutContext) => {
            const containerId = taskId ?? '';

            dispatch?.(
                selectElementChip({
                    elementId,
                    containerId,
                    chip: assignments.length
                        ? buildTaskItemAssignmentChipDescriptor(taskId, assignments[0])
                        : buildTaskItemAssignmentPlaceholderChipDescriptor(taskId),
                }),
            );
        },
        [dispatch, elementId],
    );

    const commonExtensions = useClientCommonTiptapEditorExtensions({
        saveContent,
        isEditable,
        dispatch,
        onHighlightsChanged,
        onEmptyBackspace,
        isEmptyDeleteTarget: isSingleEmptyTaskListEditor,
        placeholderOptions: {
            placeholder: taskListPlaceholder,
            showOnlyWhenEditable: false,
            showOnlyCurrent: false,
            includeChildren: true,
        },
    });

    return useMemo(
        () => [
            TaskListDocument,
            TaskListTitle,
            TaskList,
            // Supplies a stable per-task `taskId` attr used to key chip selection/editing.
            // Mirrors the card editor's use of UniqueID for stable node keys.
            UniqueID.configure({
                attributeName: 'taskId',
                types: ['taskItem'],
                generateID: () => `task-${randomString(14)}`,
            }),
            TaskListItem.configure({
                DeleteIconComponent: TaskTrashIcon,
                DragHandleComponent: TaskListDragHandle,
                ChipsComponent: TaskItemChipsContainer,
                canModifyContent,
                elementId,
                isElementSingleSelected,
                nested: true,
                onAssignmentShortcut,
            }),
            ListKeymap,
            ...commonExtensions,
        ],
        [canModifyContent, commonExtensions, elementId, isElementSingleSelected, onAssignmentShortcut],
    );
};

export default useTaskListEditorExtensions;
